import React, { useState, useEffect } from 'react';
import '../styles/OvertimeSettings.css';
import { getExtraPayConfig, updateExtraPayConfig } from '../utils/api';

const OvertimeSettings = () => {
  // 각 조건별 상태 관리
  const [overtimeConditions, setOvertimeConditions] = useState({
    extendedWork: false,     // 연장 근무 수당
    nightShift: false,       // 야간 근무 수당
    holidayWork: false,      // 휴일 근무 수당
    weeklyHoliday: false     // 주휴 수당
  });
  
  // 공제 설정 상태 관리
  const [deductionSettings, setDeductionSettings] = useState({
    insurance: false,       // 4대보험
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [companyCode, setCompanyCode] = useState('');

  // 페이지 로드 시 서버에서 특근 설정 가져오기
  useEffect(() => {
    const fetchExtraPayConfig = async () => {
      try {
        // 로컬 스토리지에서 사용자 정보 및 토큰 확인
        const token = localStorage.getItem('token');
        if (!token) {
          setError('로그인이 필요합니다.');
          setLoading(false);
          return;
        }
        
        // userInfo 객체 구조 확인 및 디버깅
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        console.log('저장된 사용자 정보:', userInfo);
        
        // 회사 코드 찾기
        let foundCompanyCode = userInfo.middleCompanyCode;
        
        if (!foundCompanyCode) {
          setError('회사 코드를 찾을 수 없습니다.');
          setLoading(false);
          return;
        }
        
        setCompanyCode(foundCompanyCode);
        console.log('사용할 회사 코드:', foundCompanyCode);
        const response = await getExtraPayConfig(foundCompanyCode);
        
        if (response.status === 'success' && response.data) {
          setOvertimeConditions({
            extendedWork: response.data.overtime_pay_enabled || false,
            nightShift: response.data.night_pay_enabled || false,
            holidayWork: response.data.holiday_pay_enabled || false,
            weeklyHoliday: response.data.weekly_holiday_pay_enabled || false
          });
          
          // 공제 설정 데이터 설정 - 필드명 수정
          setDeductionSettings({
            insurance: response.data.social_insurance_enabled || false
          });
        }
      } catch (err) {
        console.error('특근 설정 로드 오류:', err);
        setError('특근 설정을 로드하는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchExtraPayConfig();
  }, []);

  // 토글 스위치 변경 핸들러 수정
  const handleToggleChange = async (condition) => {
    try {
      // 먼저 UI 상태 업데이트
      const newValue = !overtimeConditions[condition];
      setOvertimeConditions({
        ...overtimeConditions,
        [condition]: newValue
      });
      
      // 올바른 API 필드명 매핑
      const apiFieldMapping = {
        extendedWork: 'overtime_pay_enabled',
        nightShift: 'night_pay_enabled',
        holidayWork: 'holiday_pay_enabled',
        weeklyHoliday: 'weekly_holiday_pay_enabled'
      };
      
      setUpdateLoading(true);
      
      // API 호출을 위한 데이터 준비
      const updateData = {
        [apiFieldMapping[condition]]: newValue
      };
      
      console.log('업데이트할 데이터:', updateData);
      
      // API 호출
      const response = await updateExtraPayConfig(companyCode, updateData);
      console.log('특근 설정 업데이트 성공:', response);
      
    } catch (error) {
      console.error('특근 설정 업데이트 오류:', error);
      // 에러 발생 시 UI 상태 롤백
      setOvertimeConditions({
        ...overtimeConditions
      });
      alert('설정 변경 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  // 공제 설정 토글 변경 핸들러 수정
  const handleDeductionToggleChange = async (setting) => {
    try {
      // UI 상태 업데이트
      const newValue = !deductionSettings[setting];
      setDeductionSettings({
        ...deductionSettings,
        [setting]: newValue
      });
      
      setUpdateLoading(true);
      
      // API 호출을 위한 데이터 준비 - 필드명 수정
      const updateData = {
        social_insurance_enabled: newValue
      };
      
      console.log('공제 설정 업데이트 데이터:', updateData);
      
      // API 호출
      const response = await updateExtraPayConfig(companyCode, updateData);
      console.log('공제 설정 업데이트 성공:', response);
      
    } catch (error) {
      console.error('공제 설정 업데이트 오류:', error);
      // 에러 발생 시 UI 상태 롤백
      setDeductionSettings({
        ...deductionSettings
      });
      alert('설정 변경 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) return <div>로딩 중...</div>;
  if (error) return <div>오류: {error}</div>;

  return (
    <div className="overtimeSettingsContainer">
      <h1 className="pageTitle">특근 수당 조건 설정</h1>

      {updateLoading && <div className="updateLoader">설정 저장 중...</div>}

      <div className="conditionsList">
        {/* 연장 근무 수당 */}
        <div className="conditionItem">
          <div className="conditionInfo">
            <div className="conditionText">연장 근무 수당</div>
            <div className="conditionDescription">근무 시간 8시간 초과 시 시급의 1.5배 지급</div>
          </div>
          <label className="toggleSwitch">
            <input 
              type="checkbox" 
              checked={overtimeConditions.extendedWork}
              onChange={() => handleToggleChange('extendedWork')}
              disabled={updateLoading}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {/* 야간 근무 수당 */}
        <div className="conditionItem">
          <div className="conditionInfo">
            <div className="conditionText">야간 근무 수당</div>
            <div className="conditionDescription">오후 10시 ~ 오전 6시 사이 근무 시 시급의 1.5배 지급</div>
          </div>
          <label className="toggleSwitch">
            <input 
              type="checkbox" 
              checked={overtimeConditions.nightShift}
              onChange={() => handleToggleChange('nightShift')}
              disabled={updateLoading}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {/* 휴일 근무 수당 */}
        <div className="conditionItem">
          <div className="conditionInfo">
            <div className="conditionText">휴일 근무 수당</div>
            <div className="conditionDescription">휴일에 근무 시 시급의 1.5배 지급</div>
          </div>
          <label className="toggleSwitch">
            <input 
              type="checkbox" 
              checked={overtimeConditions.holidayWork}
              onChange={() => handleToggleChange('holidayWork')}
              disabled={updateLoading}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {/* 주휴 수당 */}
        <div className="conditionItem">
          <div className="conditionInfo">
            <div className="conditionText">주휴 수당</div>
            <div className="conditionDescription">1주 소정 근무시간(15시간 이상) 충족 및 개근 시 평균 1일분 임금 지급</div>
          </div>
          <label className="toggleSwitch">
            <input 
              type="checkbox" 
              checked={overtimeConditions.weeklyHoliday}
              onChange={() => handleToggleChange('weeklyHoliday')}
              disabled={updateLoading}
            />
            <span className="slider round"></span>
          </label>
        </div>
      </div>

      {/* 공제 설정 섹션 */}
      <h1 className="pageTitle sectionTitle">공제 설정</h1>
      <div className="conditionsList">
        {/* 4대보험 */}
        <div className="conditionItem">
          <div className="conditionInfo">
            <div className="conditionText">4대보험</div>
            <div className="conditionDescription">근로자 부담률 9.399%</div>
          </div>
          <label className="toggleSwitch">
            <input 
              type="checkbox" 
              checked={deductionSettings.insurance}
              onChange={() => handleDeductionToggleChange('insurance')}
              disabled={updateLoading}
            />
            <span className="slider round"></span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default OvertimeSettings; 